import React, { useRef, useState, useEffect } from 'react'
import { FirebaseError } from 'firebase/app'
import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Link,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth'
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore'
import { getStorage, ref, uploadString } from 'firebase/storage'
import { httpsCallable, getFunctions } from 'firebase/functions'
import ReactSignatureCanvas from 'react-signature-canvas'
import parse from 'html-react-parser'

import AuthHeader from '../../components/AuthHeader'
import BasicDateStringPicker from '../../components/BasicDateStringPicker'
import useHandleError from '../../hooks/useHandleError'
import { contactEmail } from '../../utils/contactEmail'
import isRawDateValid from '../../utils/isRawDateValid'
import { isDateEmpty } from '../../utils/dateValidation'
import { validateAge } from '../../utils/ageValidation'
import { primaryServices } from '../../utils/firebaseServiceContext'

type ContentBlock = {
  id: string
  text: string
}

export default function SignUpPage() {
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [checked, setChecked] = useState(false)
  const [esign, setEsign] = useState('')
  const [termsContent, setTermsContent] = useState<string | null>(null)

  const sigRef = useRef<ReactSignatureCanvas | null>(null)
  const toast = useToast()
  const navigate = useNavigate()
  const handleError = useHandleError()

  // 1. Fetch Terms of Service content FIRST.
  //    We do NOT render the sign-up form until this completes.
  useEffect(() => {
    const fetchTermsContent = async () => {
      try {
        const db = primaryServices.firestore
        const docRef = doc(db, 'content', 'termsOfService')
        const docSnap = await getDoc(docRef)

        if (docSnap.exists()) {
          const contentBlocks: ContentBlock[] =
            docSnap.data().contentBlocks || []
          const termsBlock = contentBlocks.find(
            (block) => block.id === 'Terms of Service',
          )
          if (termsBlock) {
            setTermsContent(termsBlock.text)
          } else {
            // If the document exists but no "Terms of Service" block is found
            setTermsContent('')
          }
        } else {
          // If the doc does not exist
          setTermsContent('')
        }
      } catch (error) {
        console.error('Error fetching terms of service:', error)
        setTermsContent('')
      }
    }

    fetchTermsContent()
  }, [])

  // 2. Show a loading spinner or placeholder until termsContent is loaded
  if (termsContent === null) {
    return (
      <Container>
        <Stack spacing={4} py={8} align='center'>
          <AuthHeader />
          <Text>Loading Content...</Text>
          <Spinner />
        </Stack>
      </Container>
    )
  }

  // 3. Once we have the TOS content (even if empty), render the sign-up form
  const signUp = async () => {
    if (!email) {
      return toast({
        status: 'warning',
        title: 'Missing email',
        description: 'Please enter an email address.',
        isClosable: true,
      })
    }
    if (!password) {
      return toast({
        status: 'warning',
        title: 'Missing password',
        description: 'Please enter a password.',
        isClosable: true,
      })
    }
    if (password.length < 6) {
      return toast({
        status: 'warning',
        title: 'Weak password',
        description: 'Password should be at least 6 characters.',
        isClosable: true,
      })
    }
    if (password !== confirmPassword) {
      return toast({
        status: 'warning',
        title: 'Passwords do not match',
        description: 'Password fields should match.',
        isClosable: true,
      })
    }

    if (!firstName || !lastName) {
      return toast({
        status: 'warning',
        title: 'Missing profile information',
        description: 'Please provide your first name and last name.',
        isClosable: true,
      })
    }

    // Validate birth date is provided and complete
    if (!birthDate || isDateEmpty(birthDate)) {
      return toast({
        status: 'error',
        title: 'Missing Birth Date',
        description: 'Please select a complete birth date.',
        duration: 5000,
        isClosable: true,
      })
    }

    // Validate birth date format
    if (!isRawDateValid(birthDate)) {
      return toast({
        status: 'error',
        title: 'Validation Error',
        description: 'Invalid birth date format. Please use YYYY-MM-DD.',
        duration: 5000,
        isClosable: true,
      })
    }

    // Validate age is between 1 and 120 years
    const ageValidation = validateAge(birthDate)
    if (!ageValidation.isValid) {
      return toast({
        status: 'error',
        title: 'Validation Error',
        description: ageValidation.errorMessage,
        duration: 5000,
        isClosable: true,
      })
    }

    if (!checked) {
      return toast({
        status: 'warning',
        title: 'Terms of Service',
        description: 'Please check the box to accept our Terms of Service.',
        isClosable: true,
      })
    }

    const signature = sigRef.current?.toDataURL()
    if (!esign) {
      if (!signature || sigRef.current?.isEmpty()) {
        return toast({
          status: 'warning',
          title: 'Signature Required',
          description:
            'Please sign to confirm that you agree to the Terms of Service.',
          isClosable: true,
        })
      }
    }

    setLoading(true)

    try {
      // Check for duplicate profile first
      const functions = primaryServices.functions

      const checkDuplicateProfile = httpsCallable(
        functions,
        'checkDuplicateProfileCallable',
      )

      try {
        await checkDuplicateProfile({
          firstName,
          lastName,
          birthDate,
        })
      } catch (error: unknown) {
        setLoading(false)
        // Normalize error into a string message
        let errorMessage: string
        if (typeof error === 'string') {
          errorMessage = error
        } else if (error instanceof Error) {
          errorMessage = error.message
        } else if (
          error &&
          typeof error === 'object' &&
          'message' in error &&
          typeof (error as any).message === 'string'
        ) {
          errorMessage = (error as any).message
        } else {
          errorMessage = JSON.stringify(error)
        }

        // Detect CORS or network errors by searching normalized message
        if (
          errorMessage.includes('CORS') ||
          errorMessage.includes('Access-Control-Allow-Origin') ||
          /net::ERR_FAILED|NetworkError|ERR_CONNECTION_REFUSED/.test(
            errorMessage,
          )
        ) {
          console.warn(
            'CORS issue detected, checking for duplicates directly in Firestore',
            error,
          )

          // Skip HTTP fallback and go directly to Firestore check
          try {
            const db = primaryServices.firestore
            const usersCollection = collection(db, 'users')

            // Query for duplicate profile
            const searchFirstName = firstName.toLowerCase()
            const searchLastName = lastName.toLowerCase()

            const duplicateQuery = query(
              usersCollection,
              where('profile.searchFirstName', '==', searchFirstName),
              where('profile.searchLastName', '==', searchLastName),
              where('profile.birthDate', '==', birthDate),
            )

            const querySnapshot = await getDocs(duplicateQuery)

            if (!querySnapshot.empty) {
              return toast({
                status: 'error',
                title: 'Registration Error',
                description:
                  'That first name, last name and date of birth already exists. Contact info@healingals.org for assistance',
                isClosable: true,
              })
            }

            // No duplicate found, proceed with signup
            const {
              user: { uid },
            } = await createUserWithEmailAndPassword(
              primaryServices.auth,
              email,
              password,
            )

            // Initialize the user profile with emailVerified: false for OTP verification
            const userRef = doc(db, 'users', uid)
            await setDoc(
              userRef,
              {
                profile: {
                  firstName,
                  lastName,
                  searchFirstName,
                  searchLastName,
                  birthDate,
                },
                emailVerified: false,
              },
              { merge: true },
            )

            // Send OTP email for verification (uses shared hook logic)
            try {
              const functions = primaryServices.functions
              const sendOTPCallable = httpsCallable(
                functions,
                'sendOTPCallable',
              )
              await sendOTPCallable()
            } catch (otpError) {
              console.error('Failed to send OTP email:', otpError)
              // Continue anyway - user can resend from verify page
            }

            // Handle signature upload (async, don't wait)
            const signature = sigRef.current?.toDataURL()
            if (signature) {
              uploadString(
                ref(primaryServices.storage, `users/${uid}/signature.png`),
                signature.replace('data:image/png;base64,', ''),
                'base64',
                { contentType: 'image/png' },
              ).catch(console.error)
            }

            // Navigate to email verification page
            navigate('/verify-email')
            return
          } catch (firestoreError) {
            // If Firestore check fails too, log and show generic error
            console.error('Firestore duplicate check failed:', firestoreError)
            handleError(firestoreError)
            return
          }
        }

        // Handle the specific "already exists" error from our cloud function
        const errorCode =
          error && typeof error === 'object' && 'code' in error
            ? (error as any).code
            : undefined
        if (
          errorCode === 'functions/already-exists' ||
          errorMessage.includes('already exists')
        ) {
          return toast({
            status: 'error',
            title: 'Registration Error',
            description:
              'That first name, last name and date of birth already exists. Contact info@healingals.org for assistance',
            isClosable: true,
          })
        }
        // Handle other errors from the function call
        handleError(error)
        return
      }

      // Create the user in Firebase Auth
      const {
        user: { uid },
      } = await createUserWithEmailAndPassword(
        primaryServices.auth,
        email,
        password,
      )

      // Initialize the user profile with the validated data and emailVerified: false
      const db = primaryServices.firestore
      const userRef = doc(db, 'users', uid)
      await setDoc(
        userRef,
        {
          profile: {
            firstName,
            lastName,
            // Store lowercase versions for case-insensitive search
            searchFirstName: firstName.toLowerCase(),
            searchLastName: lastName.toLowerCase(),
            birthDate,
            // Explicitly set to false so ProfileCompletionGuard enforces profile completion
            agreedToProfileCheckbox: false,
          },
          emailVerified: false,
        },
        { merge: true },
      )

      // Send OTP email for verification
      try {
        const sendOTPCallable = httpsCallable(functions, 'sendOTPCallable')
        await sendOTPCallable()
      } catch (otpError) {
        console.error('Failed to send OTP email:', otpError)
        // Continue anyway - user can resend from verify page
      }

      // Navigate to email verification page
      navigate('/verify-email')

      // If we have a signature, upload it to Firebase Storage (async, don't wait)
      if (signature) {
        uploadString(
          ref(primaryServices.storage, `users/${uid}/signature.png`),
          signature.replace('data:image/png;base64,', ''),
          'base64',
          { contentType: 'image/png' },
        ).catch(console.error)
      }
    } catch (e: unknown) {
      setLoading(false)
      if (e instanceof FirebaseError) {
        // Handle common Firebase Auth errors
        let description = e.message
        switch (e.code) {
          case 'auth/email-already-in-use':
            description = 'That email address is already in use.'
            break
          case 'auth/invalid-email':
            description = 'Please enter a valid email address.'
            break
          case 'auth/weak-password':
            description = 'Password must be at least 6 characters.'
            break
        }
        toast({
          status: 'error',
          title: 'Sign Up Error',
          description,
          isClosable: true,
        })
      } else {
        handleError(e)
      }
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        signUp()
      }}
    >
      <Container>
        <Stack spacing={4}>
          <AuthHeader />
          <Text textAlign='center' fontSize='medium'>
            Have an account?{' '}
            <Link as={RouterLink} to='/sign-in' color='purple.600'>
              Sign In
            </Link>
          </Text>

          <Alert
            borderRadius='md'
            variant='subtle'
            backgroundColor='purple.100'
          >
            <AlertDescription>
              This Registry is for those diagnosed with ALS or an ALS-related
              disorder. Please put in the PALS name and signature, not the CALS.
              Medical professionals or researchers may also register to see what
              info is being asked of their ALS patients.
            </AlertDescription>
          </Alert>

          {/* Email & Password fields */}
          <Stack spacing={2}>
            <Input
              placeholder='Email'
              type='email'
              autoComplete='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              placeholder='Password'
              type='password'
              autoComplete='new-password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Input
              placeholder='Confirm Password'
              type='password'
              autoComplete='new-password'
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </Stack>

          {/* Profile Information Fields */}
          <Stack spacing={2}>
            <Text textStyle='3x1' fontWeight='bold'>
              Profile Information
            </Text>
            <FormControl isRequired>
              <FormLabel>First Name</FormLabel>
              <Input
                placeholder='First Name'
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Last Name</FormLabel>
              <Input
                placeholder='Last Name'
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Birth Date</FormLabel>
              <BasicDateStringPicker
                startYear='1930'
                endYear={new Date().getFullYear().toString()}
                dateString={birthDate}
                setDateString={setBirthDate}
              />
            </FormControl>
          </Stack>

          {/* Terms of Service Content (now only rendered once loaded) */}
          <Stack spacing={2}>
            <Text textStyle='3x1' fontWeight='bold'>
              Terms of Service
            </Text>
            <Box
              maxH='400px'
              overflowY='auto'
              p={4}
              borderWidth={1}
              borderRadius='md'
              bg='gray.50'
              sx={{
                '& ul, & ol': {
                  paddingLeft: '2rem',
                  marginTop: '0.5rem',
                  marginBottom: '0.5rem',
                },
                '& li': {
                  marginBottom: '0.5rem',
                },
              }}
            >
              {parse(termsContent)}
            </Box>
          </Stack>

          {/* User must accept TOS */}
          <Stack spacing={2}>
            <Checkbox
              isChecked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            >
              I have read and agree to the Terms of Service above.
            </Checkbox>

            <Text>Please sign:</Text>
            <Stack spacing={1}>
              <Stack
                borderWidth={1}
                borderColor='gray.600'
                borderStyle='dashed'
                rounded='md'
              >
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore - react-signature-canvas types incompatible with React 18 */}
                <ReactSignatureCanvas
                  ref={sigRef}
                  penColor='black'
                  canvasProps={{ height: 200 }}
                />
              </Stack>
              <Flex justify='end'>
                <Button
                  size='xs'
                  onClick={() => {
                    sigRef.current?.clear()
                  }}
                  colorScheme='gray'
                  variant='ghost'
                >
                  Clear
                </Button>
              </Flex>
            </Stack>

            <Stack direction='row'>
              <Text fontWeight='bold'>OR</Text>
              <Text>
                I agree that my typed name below serves as my electronic
                signature
              </Text>
            </Stack>
            <Input
              placeholder='E-Signature (Full-name)'
              value={esign}
              onChange={(e) => setEsign(e.target.value)}
            />
          </Stack>

          {/* Submit */}
          <Button mt={3} type='submit' w='full' isLoading={loading}>
            Sign Up
          </Button>

          <Text fontSize='medium'>
            Have an account?{' '}
            <Link as={RouterLink} to='/sign-in' color='purple.600'>
              Sign In
            </Link>
          </Text>
          <Text>
            For questions about the registry, please email us at:{' '}
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
          </Text>
        </Stack>
      </Container>
    </form>
  )
}
